// Image Optimizer Service - Resize and compress images locally to reduce storage and bandwidth

import { Injectable } from '@angular/core';

export interface ImageOptimizerOptions {
  /** Max width/height in pixels (default 1200) */
  maxDimension?: number;
  /** JPEG/WebP quality 0–1 (default 0.85) */
  quality?: number;
  /** Max file size in bytes (optional; will reduce quality if exceeded) */
  maxSizeBytes?: number;
  /** Output MIME type: image/jpeg or image/webp (default image/jpeg) */
  outputMime?: 'image/jpeg' | 'image/webp';
}

const DEFAULT_OPTIONS: Required<Omit<ImageOptimizerOptions, 'maxSizeBytes'>> & { maxSizeBytes?: number } = {
  maxDimension: 1200,
  quality: 0.85,
  outputMime: 'image/jpeg',
  maxSizeBytes: 800 * 1024, // 800 KB target
};

@Injectable({
  providedIn: 'root',
})
export class ImageOptimizerService {
  /**
   * Optimize an image file: resize to fit within maxDimension and compress.
   * Returns a new File (JPEG or WebP) suitable for upload.
   */
  async optimize(file: File, options: ImageOptimizerOptions = {}): Promise<File> {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    const maxDim = opts.maxDimension ?? 1200;
    const quality = Math.min(1, Math.max(0, opts.quality ?? 0.85));
    const mime = opts.outputMime ?? 'image/jpeg';
    const maxSize = opts.maxSizeBytes;

    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width >= height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas 2d not available'));
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);

        const extension = mime === 'image/webp' ? 'webp' : 'jpg';
        let q = quality;
        const tryBlob = (): void => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to create blob'));
                return;
              }
              if (maxSize && blob.size > maxSize && q > 0.3) {
                q = Math.max(0.3, q - 0.15);
                tryBlob();
                return;
              }
              const name = file.name.replace(/\.[^.]+$/, '') || 'image';
              const optimized = new File([blob], `${name}.${extension}`, {
                type: mime,
                lastModified: Date.now(),
              });
              resolve(optimized);
            },
            mime,
            q
          );
        };
        tryBlob();
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };
      img.src = url;
    });
  }
}
